import React, { useEffect, useState } from 'react';
import { getShareLinkByToken, incrementShareLinkViews } from './db';
import { supabase } from './supabase';
import { calculateReadinessScore } from './utils/readinessScore';
import ReadinessGauge from './ReadinessGauge';

function StatusBadge({ status }) {
  const map = {
    yes:     { label: 'Implemented',         bg: 'var(--green-bg)',  color: 'var(--green)', },
    partial: { label: 'Partially Impl.',      bg: 'rgba(245,158,11,0.1)', color: '#f59e0b',  },
    no:      { label: 'Not Implemented',      bg: 'var(--red-bg)',   color: 'var(--red)',   },
    na:      { label: 'N/A',                  bg: 'var(--bg2)',      color: 'var(--text3)', },
  };
  const s = map[status] || map.na;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.color}33`,
    }}>
      {s.label}
    </span>
  );
}

export default function PublicReport() {
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [link, setLink]         = useState(null);
  const [data, setData]         = useState(null);
  const [readiness, setReadiness] = useState(null);

  const token = window.location.pathname.replace('/report/', '').replace(/\//g, '');

  useEffect(() => {
    if (!token) { setError('Invalid link.'); setLoading(false); return; }
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function loadReport() {
    try {
      const shareLink = await getShareLinkByToken(token);
      if (!shareLink) { setError('This link is invalid or has expired.'); setLoading(false); return; }

      setLink(shareLink);
      await incrementShareLinkViews(shareLink.id);

      const userId = shareLink.user_id;

      // Fetch assessment answers
      const { data: assessments } = await supabase
        .from('assessments').select('control_id, answer').eq('user_id', userId);
      const answersMap = {};
      (assessments || []).forEach(r => { answersMap[r.control_id] = r.answer; });

      // Fetch evidence
      let evidence = [];
      if (shareLink.show_evidence) {
        const { data: ev } = await supabase.from('evidence').select('*').eq('user_id', userId);
        evidence = ev || [];
      }

      // Fetch tasks
      let tasks = [];
      if (shareLink.show_tasks) {
        const { data: tk } = await supabase.from('tasks').select('*').eq('user_id', userId);
        tasks = tk || [];
      }

      // Fetch narratives
      const { data: narrs } = await supabase.from('control_narratives')
        .select('*').eq('user_id', userId).eq('framework', shareLink.framework);

      // Profile
      const { data: profile } = await supabase.from('profiles').select('company_name').eq('id', userId).single();

      // Build control list for gauge
      const controlsList = Object.entries(answersMap).map(([id, answer]) => ({ id, answer }));
      const affirmations = [];
      const result = calculateReadinessScore(controlsList, evidence, tasks, affirmations, narrs || []);

      setData({ answersMap, evidence, tasks, profile, narrs: narrs || [] });
      setReadiness(result);
    } catch (err) {
      setError('Failed to load report.');
      console.error(err);
    }
    setLoading(false);
  }

  if (loading) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--text3)' }}>
      Loading report…
    </div>
  );

  if (error) return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', gap: 12 }}>
      <div style={{ fontSize: 40 }}>🔒</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Link Unavailable</div>
      <div style={{ fontSize: 14, color: 'var(--text3)' }}>{error}</div>
    </div>
  );

  const total   = Object.keys(data.answersMap).length;
  const impl    = Object.values(data.answersMap).filter(v => v === 'yes').length;
  const partial = Object.values(data.answersMap).filter(v => v === 'partial').length;
  const notImpl = Object.values(data.answersMap).filter(v => v === 'no').length;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: 'var(--font)', color: 'var(--text)' }}>
      {/* Header */}
      <div style={{
        background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
        padding: '0 40px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 7, background: 'linear-gradient(135deg, #2D7DD2, #1A4E8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff' }}>CM</div>
          <span style={{ fontSize: 15, fontWeight: 600 }}>TheClearAudit</span>
          <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 8 }}>Shared Compliance Report</span>
        </div>
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>View-only · {link?.label}</span>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '36px 24px 80px' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
          {data.profile?.company_name || 'Organization'} — Compliance Report
        </h1>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 32 }}>
          Framework: <strong>{link?.framework?.toUpperCase().replace('_', ' ')}</strong> · Generated {new Date().toLocaleDateString()}
        </div>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 32 }}>
          {[
            { label: 'Total Controls', val: total, color: 'var(--text)' },
            { label: 'Implemented',    val: impl,  color: 'var(--green)' },
            { label: 'Partial',        val: partial, color: '#f59e0b' },
            { label: 'Not Impl.',      val: notImpl, color: 'var(--red)' },
          ].map(c => (
            <div key={c.label} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 20px' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.val}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Readiness gauge */}
        {link?.show_score && readiness && (
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '28px 32px', marginBottom: 32, display: 'flex', gap: 40, alignItems: 'center', flexWrap: 'wrap' }}>
            <ReadinessGauge result={readiness} size={160} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Overall Readiness Score</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
                This score reflects control implementation, evidence coverage, remediation progress, documented narratives, and affirmation status. Scores of 85%+ indicate readiness for a formal CMMC assessment.
              </div>
            </div>
          </div>
        )}

        {/* Controls list */}
        {link?.show_controls && (
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Control Status</div>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {Object.entries(data.answersMap).map(([id, status], i) => (
                <div key={id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: i < Object.keys(data.answersMap).length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{id}</span>
                  <StatusBadge status={status} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Evidence */}
        {link?.show_evidence && data.evidence.length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Evidence ({data.evidence.length} items)</div>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {data.evidence.map((ev, i) => (
                <div key={ev.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: i < data.evidence.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <span style={{ fontSize: 13 }}>{ev.title || ev.file_name}</span>
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>{ev.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tasks */}
        {link?.show_tasks && data.tasks.length > 0 && (
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Remediation Tasks ({data.tasks.length})</div>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {data.tasks.map((task, i) => (
                <div key={task.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: i < data.tasks.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <span style={{ fontSize: 13 }}>{task.title}</span>
                  <span style={{ fontSize: 12, color: 'var(--text3)', textTransform: 'capitalize' }}>{task.status?.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 48, padding: '16px 24px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>
          This report was generated by TheClearAudit and shared via a guest link. It is read-only. Data is current as of generation time.
        </div>
      </div>
    </div>
  );
}
